/**
 * <video> behaviour for the media block: autoplay on screen, the mute button, and
 * swapping the poster out once the first frame is ready.
 *
 * Was `snippets/video-file.liquid`'s {% javascript %} tag, which rode the shared
 * `snippet-scripts.js` onto every page of the store. Loaded from the snippet now, so
 * only pages that actually render a video file pay the 13 KB.
 *
 * The guard matters more here than elsewhere: the snippet renders once per video, so
 * a page with three of them emits three identical <script src> tags, and every one of
 * them executes. Without this each run would build its own WeakSets and bind the same
 * <video> again.
 */
(function () {
  if (window.__videoFileInit) return;
  window.__videoFileInit = true;

  // The hero slider clones slides to loop. A clone carries every data-* attribute of
  // the original, so a "bound" marker written back to the DOM would make an untouched
  // clone look initialized. Track bound nodes by identity instead.
  var boundVideos = new WeakSet();
  var boundButtons = new WeakSet();
  var boundPosters = new WeakSet();

  function videoFor(btn) {
    var wrapper = btn.closest('.video-file-wrapper');
    var scoped = wrapper && wrapper.querySelector('.video-file');
    // Cloned slides duplicate the id, so prefer the video in the same wrapper.
    return scoped || document.getElementById(btn.dataset.videoId);
  }

  // The two icons are <svg>, and `hidden` is an HTMLElement property — on an SVG
  // element `el.hidden = true` silently sets a plain JS expando and never reaches
  // the DOM, so the attribute selector that hides it never matches and both icons
  // stay painted. Writing the attribute is the part that actually works.
  function setHidden(el, hide) {
    if (!el) return;
    if (hide) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  // One video the visitor sees is several DOM nodes: a slider in loop mode
  // clones the slide, and every clone carries its own <video> under the same id.
  // Treating those copies separately is what made the sound behave on its own —
  // unmuting the copy on screen left the others muted, so the next turn of the
  // carousel came back silent and the turn after that, back on the copy that was
  // unmuted, started speaking again. Anything to do with sound therefore applies
  // to the whole group, keyed by that shared id.
  function copiesOf(video) {
    if (!video.id) return [video];
    return [].filter.call(document.querySelectorAll('.video-file'), function (v) {
      return v.id === video.id;
    });
  }

  // What the visitor asked for, kept apart from what any one element is doing
  // right now. `muted` on the element is transient — it is forced back on the
  // moment the video leaves the screen — so the choice has to live somewhere
  // else or it would be lost on every turn of the carousel. Keyed by the shared
  // id, which the snippet always renders.
  var soundWanted = {};

  function wantsSound(video) {
    return soundWanted[video.id] === true;
  }

  function syncIcons(id) {
    var on = soundWanted[id] === true;
    [].forEach.call(document.querySelectorAll('.video-file__mute-btn'), function (btn) {
      if (btn.dataset.videoId !== id) return;
      setHidden(btn.querySelector('.video-file__icon--muted'), on);
      setHidden(btn.querySelector('.video-file__icon--unmuted'), !on);
    });
  }

  function setSound(video, on) {
    soundWanted[video.id] = on;
    copiesOf(video).forEach(function (v) {
      // Sound is granted only to a copy that is actually playing, which means
      // the one on screen. A paused copy stays muted, so nothing can be heard
      // coming from a slide that is out of view.
      v.muted = on ? v.paused : true;
    });
    syncIcons(video.id);
  }

  function silence(video) {
    video.pause();
    // Muted as well as paused: pausing alone has lost the race against a copy
    // that was still starting up, and the symptom is a video you cannot see
    // talking over the one you can.
    video.muted = true;
  }

  function bindMuteButton(btn) {
    if (boundButtons.has(btn)) return;
    boundButtons.add(btn);

    btn.addEventListener('click', function () {
      var video = videoFor(btn);
      if (!video) return;
      setSound(video, !wantsSound(video));
    });
  }

  // Promotes a deferred poster into the CSS variables the stylesheet reads.
  // Called at the same moment the video starts fetching, so the poster is never
  // downloaded for a slide nobody reached. It still lands before the first frame
  // decodes, which is the gap it exists to cover — the image is much smaller
  // than the video.
  //
  // Both variables are written, desktop and mobile. That costs one request, not
  // two: the browser only fetches the background-image the media query actually
  // resolves to, and the other value sits there as an unused token.
  //
  // Applied to every copy: the slider clones its slides, and a clone that comes
  // into view needs its own variables set. The data attributes are dropped so
  // this is idempotent under the repeated observer callbacks.
  function hydratePoster(video) {
    copiesOf(video).forEach(function (v) {
      var url = v.getAttribute('data-poster');
      var urlMobile = v.getAttribute('data-poster-mb');
      if (!url && !urlMobile) return;
      if (urlMobile || url) {
        v.style.setProperty('--video-poster', 'url("' + (urlMobile || url) + '")');
      }
      if (url) v.style.setProperty('--video-poster-d', 'url("' + url + '")');
      v.removeAttribute('data-poster');
      v.removeAttribute('data-poster-mb');
    });
  }

  // The poster is a background, so unlike a native `poster` the browser does not
  // retire it on its own — it would stay behind the video for the life of the
  // page. `loadeddata` is the first moment a frame exists to cover it. Bound to
  // every video, autoplay or not, and never on `error`: a video that failed to
  // load is exactly when keeping the still is worth it.
  function bindPosterCleanup(video) {
    if (boundPosters.has(video)) return;
    boundPosters.add(video);

    if (video.readyState >= 2) {
      video.classList.add('video-file--frame-ready');
      return;
    }
    video.addEventListener(
      'loadeddata',
      function () {
        video.classList.add('video-file--frame-ready');
      },
      { once: true }
    );
  }

  function play(video) {
    hydratePoster(video);

    // A slide transition puts the outgoing and incoming slides on screen at the
    // same time, so both copies of the video are briefly intersecting and both
    // would start — two of the same soundtrack a beat apart. The copy taking over
    // silences its siblings first.
    copiesOf(video).forEach(function (v) {
      if (v !== video) silence(v);
    });

    // Restores the earlier choice: the same video coming back into view comes
    // back with the sound the visitor left it with.
    video.muted = !wantsSound(video);

    var started = video.play();
    if (!started) return;
    started.catch(function () {
      if (!video.muted) {
        // Starting with sound can be refused when there is no recent user
        // gesture. Seeing the video matters more than hearing it, so drop back
        // to a muted play — and correct the button, which would otherwise
        // promise sound that is not coming.
        soundWanted[video.id] = false;
        syncIcons(video.id);
        video.muted = true;
        var retried = video.play();
        if (retried) retried.catch(function () { showFirstFrame(video); });
        return;
      }
      showFirstFrame(video);
    });
  }

  function showFirstFrame(video) {
    // Autoplay can be refused outright — a battery saver, a data saver, or
    // simply a background tab. With preload="none" that would leave an empty box
    // where the video should be, so pull enough of it to paint a frame. A poster
    // image on the block avoids the gap entirely and is worth setting.
    if (video.preload === 'none') {
      video.preload = 'metadata';
      video.load();
    }
  }

  // How much of the element the visitor can actually see, in square pixels.
  //
  // `entry.isIntersecting` cannot answer this. A carousel parks the next slide
  // exactly one viewport to the side, where it touches the edge and Chrome
  // reports it as intersecting at ratio 0 — and, because it never stops touching
  // that edge, no exit event ever follows. Trusting that flag is what left an
  // invisible copy of the hero video playing out loud for seconds at a time.
  function visibleArea(el) {
    var vw = document.documentElement.clientWidth;
    var vh = document.documentElement.clientHeight;
    var r = el.getBoundingClientRect();
    var w = Math.min(r.right, vw) - Math.max(r.left, 0);
    var h = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    if (w <= 0 || h <= 0) return 0;
    return w * h;
  }

  function isOnScreen(el) {
    var seen = visibleArea(el);
    if (seen <= 0) return false;
    var r = el.getBoundingClientRect();
    var own = r.width * r.height;
    var viewport = document.documentElement.clientWidth * document.documentElement.clientHeight;
    // A quarter of the element, or half the screen. The second clause covers
    // media taller than the viewport, which can never show much of itself.
    return seen >= own * 0.25 || seen >= viewport * 0.5;
  }

  // Decides, for one logical video, which of its copies is the one on screen.
  // Largest visible area wins, so during a slide transition the outgoing copy
  // keeps playing until the incoming one has more of the screen than it does,
  // rather than handing over on the first visible pixel.
  function updateGroup(video) {
    var copies = copiesOf(video);
    var winner = null;
    var best = 0;

    copies.forEach(function (v) {
      var seen = visibleArea(v);
      if (seen > best) {
        best = seen;
        winner = v;
      }
    });

    if (!winner || !isOnScreen(winner)) {
      copies.forEach(silence);
      return;
    }

    copies.forEach(function (v) {
      if (v !== winner) silence(v);
    });
    // Starts the download too: the element carries preload="none", so nothing
    // is fetched until a copy genuinely reaches the screen.
    play(winner);
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            var done = {};
            entries.forEach(function (entry) {
              var id = entry.target.id;
              if (done[id]) return;
              done[id] = true;
              updateGroup(entry.target);
            });
          },
          {
            // No rootMargin on purpose. A lead-in margin sounds helpful for a
            // video scrolled towards, but on a 412px-wide phone even 200px of it
            // reaches into the neighbouring carousel slide, which pulled the hero
            // video down right after load instead of not at all.
            rootMargin: '0px',
            // Several steps so the callback keeps arriving while a slide moves
            // across, not only as it first appears. The decision is taken by
            // measuring, but this is what wakes the decision up.
            threshold: [0, 0.25, 0.5, 0.75, 1],
          }
        )
      : null;

  var watched = [];

  function bindVideo(video) {
    if (boundVideos.has(video)) return;
    boundVideos.add(video);

    if (!observer) {
      play(video);
      return;
    }
    watched.push(video);
    observer.observe(video);
  }

  // A slider moves its slides with a transform, and a transform that starts and
  // ends outside the viewport crosses none of the thresholds above — so the
  // observer can stay silent while the picture on screen changes completely.
  // Re-deciding once the movement settles closes that gap.
  var pending = false;

  function refreshAll() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      var done = {};
      watched.forEach(function (v) {
        if (!v.isConnected || done[v.id]) return;
        done[v.id] = true;
        updateGroup(v);
      });
    });
  }

  document.addEventListener('transitionend', refreshAll, true);
  window.addEventListener('resize', refreshAll);
  window.addEventListener('orientationchange', refreshAll);

  function initButtons(root) {
    (root || document).querySelectorAll('.video-file__mute-btn').forEach(bindMuteButton);
  }

  function initPosters(root) {
    (root || document).querySelectorAll('.video-file').forEach(bindPosterCleanup);
  }

  function initVideos(root) {
    (root || document).querySelectorAll('.video-file[data-video-autoplay]').forEach(bindVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initButtons();
      initPosters();
    });
  } else {
    initButtons();
    initPosters();
  }

  // Videos deliberately wait for `load` rather than DOMContentLoaded. A slider
  // mounts on DOMContentLoaded, and until it does its slides are still laid out
  // in normal flow, where every one of them looks on-screen — observing that
  // early made the off-screen hero video download anyway, which is the whole
  // thing this is meant to prevent. Waiting also picks up the clones a slider
  // creates while mounting.
  function onPageLoaded() {
    // Buttons are re-scanned here too, not just videos: a slider clones its
    // slides while mounting, and a cloned button is a node the earlier pass
    // never saw. Miss this and the mute control of whichever clone is on screen
    // does nothing at all. The WeakSets keep the repeat pass free.
    initButtons();
    initPosters();
    initVideos();
  }

  if (document.readyState === 'complete') {
    onPageLoaded();
  } else {
    window.addEventListener('load', onPageLoaded);
  }

  // The theme editor re-renders a section without reloading the page. Without
  // this the video would sit frozen on its poster, since nothing re-observes it.
  document.addEventListener('shopify:section:load', function (event) {
    initButtons(event.target);
    initPosters(event.target);
    initVideos(event.target);
  });
})();
